import { CUTLINER_OPTS } from '@modules/Vector/S/Constant/stickerizer.constant.ts';
import type { Size } from './stickerizer.types';
import { VECTOR_ERROR_CODE } from '@modules/Vector/S/Constant/error-code';
import { VectorApiCall } from '../sizeCalculator/api/api.call';

export class StickerizerHelper {
  private debug: boolean;

  constructor(debug = true) {
    this.debug = debug;
  }

  /**
   * Convert millimeters to pixels at 300 DPI
   * @param mm - Size in millimeters
   * @returns Size in pixels at 300 DPI
   */
  static mmTo300DpiPx(mm: number): number {
    return (mm / 25.4) * CUTLINER_OPTS.DPI_300;
  }

  static pxTo300DpiMm(px: number): number {
    return (px / CUTLINER_OPTS.DPI_300) * 25.4;
  }

  /**
   * Convert Size object from millimeters to pixels at 300 DPI
   * @param size - Size object in millimeters
   * @returns Size object in pixels at 300 DPI
   */
  static mmTo300DpiPxSize(size: Size): Size {
    return {
      width: StickerizerHelper.mmTo300DpiPx(size.width),
      height: StickerizerHelper.mmTo300DpiPx(size.height),
    };
  }

  static pxTo300DpiMmSize(size: Size): Size {
    return {
      width: StickerizerHelper.pxTo300DpiMm(size.width),
      height: StickerizerHelper.pxTo300DpiMm(size.height),
    };
  }

  static roundToPrecision(value: number, precision: number): number {
    // precision 이 2라면 소수 2째자리까지 표현하는데 소수 3째자리에서 round 해서 2째자리까지 표현
    return Math.round(value * Math.pow(10, precision)) / Math.pow(10, precision);
  }

  /**
   * Generate process cache key from target size, offset distance, and smooth offset adder
   * @param targetSizeMm - Target size in millimeters
   * @param offsetDistanceMm - Offset distance in millimeters
   * @param smoothOffsetAdder - Smooth offset adder value (optional)
   * @returns Cache key string
   */
  static generateProcessCacheKey(
    targetSizeMm: Size,
    offsetDistanceMm: number,
    smoothOffsetAdder?: number,
  ): string {
    const smoothSuffix = smoothOffsetAdder !== undefined ? `_smooth${smoothOffsetAdder}` : '';
    return `${targetSizeMm.width}x${targetSizeMm.height}_${offsetDistanceMm}${smoothSuffix}`;
  }

  /**
   * Calculate expected height based on target width, maintaining aspect ratio
   * @param targetWidthMm - Target width in millimeters
   * @param offsetDistanceMm - Offset distance in millimeters
   * @param imageSize - Original image size (width, height)
   * @returns Expected height in millimeters
   */
  static calculateExpectedHeight(
    targetWidthMm: number,
    offsetDistanceMm: number,
    imageSize: { width: number; height: number },
  ): number {
    const aspectRatio = imageSize.width / imageSize.height;
    const effectiveWidth = targetWidthMm - 2 * offsetDistanceMm;
    const effectiveHeight = effectiveWidth / aspectRatio;
    return effectiveHeight + 2 * offsetDistanceMm;
  }

  /**
   * Calculate expected width based on target height, maintaining aspect ratio
   * @param targetHeightMm - Target height in millimeters
   * @param offsetDistanceMm - Offset distance in millimeters
   * @param imageSize - Original image size (width, height)
   * @returns Expected width in millimeters
   */
  static calculateExpectedWidth(
    targetHeightMm: number,
    offsetDistanceMm: number,
    imageSize: { width: number; height: number },
  ): number {
    const aspectRatio = imageSize.width / imageSize.height;
    const effectiveHeight = targetHeightMm - 2 * offsetDistanceMm;
    const effectiveWidth = effectiveHeight * aspectRatio;
    return effectiveWidth + 2 * offsetDistanceMm;
  }

  /**
   * Calculate scale factor for fitting image inside target area
   * @param targeSize - Target area size in pixels
   * @param imageSize - Original image size in pixels
   * @returns Scale factor (fit inside)
   */
  static calculateFitInsideScaleFactor(targeSize: Size, imageSize: Size): number {
    const widthScale = targeSize.width / imageSize.width;
    const heightScale = targeSize.height / imageSize.height;
    return Math.min(widthScale, heightScale);
  }

  /**
   * Create and trigger download of a file
   * @param href - URL or blob URL for download
   * @param filename - Name for the downloaded file
   */
  private triggerDownload(href: string, filename: string): void {
    if (!this.debug) return;

    const anchor = document.createElement('a');
    anchor.href = href;
    anchor.download = filename;
    anchor.target = '_blank';
    anchor.click();
  }

  /**
   * Get file extension from mimetype
   * @param mimeType - MIME type string
   * @returns File extension with dot prefix
   */
  private getExtensionFromMimeType(mimeType: string): string {
    const mimeToExtension: Record<string, string> = {
      'image/jpeg': '.jpg',
      'image/jpg': '.jpg',
      'image/png': '.png',
      'image/gif': '.gif',
      'image/webp': '.webp',
      'image/svg+xml': '.svg',
      'image/bmp': '.bmp',
      'image/tiff': '.tiff',
      'image/ico': '.ico',
      'text/plain': '.txt',
      'text/html': '.html',
      'text/css': '.css',
      'text/javascript': '.js',
      'application/javascript': '.js',
      'application/json': '.json',
      'application/pdf': '.pdf',
      'application/zip': '.zip',
      'application/xml': '.xml',
      'text/xml': '.xml',
    };

    return mimeToExtension[mimeType.toLowerCase()] || '';
  }

  /**
   * Check if filename already has an extension
   * @param filename - Filename to check
   * @returns True if filename has extension
   */
  private hasExtension(filename: string): boolean {
    const lastDotIndex = filename.lastIndexOf('.');
    return lastDotIndex > 0 && lastDotIndex < filename.length - 1;
  }

  /**
   * Download SVG content as a file
   * @param svg - SVG content string
   * @param filename - Name for the downloaded file
   */
  downloadSvgFile(svg: string, filename: string): void {
    if (!this.debug) return;

    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);

    try {
      this.triggerDownload(url, filename);
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  /**
   * Download file from URL with automatic mimetype detection and extension appending
   * @param url - File URL
   * @param filename - Base name for the downloaded file (extension will be auto-appended if needed)
   */
  async downloadFileFromUrl(url: string, filename: string): Promise<void> {
    if (!this.debug) return;

    try {
      // Fetch the file content
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
      }

      const contentType = response.headers.get('content-type') || 'application/octet-stream';
      let finalFilename = filename;

      if (!this.hasExtension(filename)) {
        const extension = this.getExtensionFromMimeType(contentType);
        if (extension) {
          finalFilename = `${filename}${extension}`;
        }
      }

      // Create blob from response and download
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      try {
        this.triggerDownload(blobUrl, finalFilename);
      } finally {
        URL.revokeObjectURL(blobUrl);
      }
    } catch (error) {
      console.warn('Failed to download file:', error);
      // Fallback to direct URL download if blob creation fails
      this.triggerDownload(url, filename);
    }
  }
}

export async function isSvgHaveEnoughInnerSpace({
  svgString,
  spaceMm, // 얼마큼의 동전 크기가 들어갈 수 있는지 여부 (지름 mm)
}: {
  svgString: string;
  spaceMm: number;
}) {
  const vectorApi = new VectorApiCall();
  const result = await vectorApi.applyOffsetFromSvg({
    svgString,
    offset: -spaceMm / 2,
    isMm: false,
    accuracy: 10,
    isRetainOriginalSvg: false,
    smoothStrength: 0,
  });

  if (result.success) {
    return true;
  } else if (result.error.code === VECTOR_ERROR_CODE.OFFSET_EMPTY_RESULT) {
    return false;
  } else {
    throw result.error;
  }
}
